import type { ReactElement } from 'react';
import React from 'react';
import clsx from 'clsx';
import { groupBy, sortBy } from 'lodash';
import { Link } from 'react-router-dom';
import ExternalLink from 'atom-components/external-link';
import IconActionLink from 'atom-components/table-outdated/icon-action-link';
import type { BaseArea } from 'services/assist-api/entities/area/areas';
import type { BaseCustomerArea } from 'services/assist-api/entities/customers-areas/customers-areas';
import { useRoleMatch } from 'services/providers/auth.hooks';
import './teacher-dashboard.scss';

type Props = {
  areas: DashboardArea[];
};

export type DashboardArea = BaseArea & {
  customerArea?: BaseCustomerArea;
  isHistoric?: boolean;
  isActive?: boolean;
};

const getTitle = (title: string): string | null => {
  switch (title) {
    case 'assessment':
      return 'Assessments';
    case 'survey':
      return 'Surveys';
    default:
      return null;
  }
};

export default function TeacherDashboard({ areas }: Props): ReactElement {
  const isSiteAdmin = useRoleMatch([], true);
  const sortedAreas = sortBy(areas, (a) => !a.isActive);
  const groupedAreas = groupBy(sortedAreas, 'type');

  return (
    <div>
      {Object.keys(groupedAreas).map((key) => {
        const areasByType = groupedAreas[key];
        const title = getTitle(key);

        return (
          <div key={`category-${key}`} className="category">
            <h1 className="category-name">{title}</h1>
            <div className="area-wrapper">
              {areasByType.map((area) => (
                <div
                  data-theme={area.identifier.replace('@', '-')}
                  className={clsx('card area-card', {
                    historic: area.isHistoric,
                    active: area.isActive
                  })}
                  key={`card-${area.id}`}
                >
                  <Link
                    to={`/product/${area.product.id}/area/${area.id}/test-instances`}
                    className="text-body h-100"
                  >
                    <span className="historic-label">Historic</span>
                    <h5 className="card-title m-auto p-1">
                      {area.alternativeName && (
                        <>
                          {area.alternativeName}
                          <br className="mb-3" />
                          {area.name}
                        </>
                      )}
                      {!area.alternativeName && area.name}
                    </h5>
                  </Link>
                  {area.isActive && (
                    <div className="badge-wrapper">
                      {isSiteAdmin && (
                        <IconActionLink
                          id={`manage-${area.id}`}
                          to={`/organisation-area-users?organisation-area=${area.customerArea?.id}&area=${area.id}`}
                          icon="user-gear"
                          size="1x"
                          buttonClassName="p-1"
                          tooltip="Manage Area"
                          className="site-admin-text"
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="category">
        <h1 className="category-name">Resources</h1>
        <div className="area-wrapper">
          <div data-theme="arbs" className="card area-card">
            <ExternalLink className="text-body h-100" href="https://arbs.nzcer.org.nz">
              <h5 className="card-title m-auto p-1">Assessment Resource Bank</h5>
            </ExternalLink>
          </div>
          <div data-theme="nzcer" className="card area-card">
            <ExternalLink className="text-body h-100" href="https://www.nzcer.org.nz/pld">
              <h5 className="card-title m-auto p-1">NZCER Workshops</h5>
            </ExternalLink>
          </div>
          <div data-theme="tsp" className="card area-card">
            <ExternalLink className="text-body h-100" href="https://www.tspsurveys.org.nz">
              <h5 className="card-title h6 m-auto p-1">
                Teaching, School, and Leadership Practices Survey
              </h5>
              <hr />
              <span>Now available for terms 2 & 3</span>
            </ExternalLink>
          </div>
        </div>
      </div>
    </div>
  );
}
